import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { usePermissions } from '../../../auth/store';
import { Can } from '../../../rbac/RequirePermission';
import { errMsg } from '../../../lib/errors';
import { QueryError } from '../../../lib/QueryState';
import { downloadText } from '../../../lib/csv';
import { plain } from '../../../lib/labels';
import { newAdjustKey } from '../../users/walletAdjust';
import { formatDuration, type ListenerOverview, type StatsRangeParams } from '../listenerStats';
import { StatsRangeFilter, useStatsRange } from '../ListenerStatsWidgets';
import { attendanceCsv } from './attendanceCsv';
import { CreditSettlementButton } from './CreditSettlementButton';
import { EmploymentHolidaysCard } from './EmploymentHolidaysCard';
import {
  BASIS_OPTIONS,
  OT_STATUS_LABEL,
  SALARY_MODEL_LABEL,
  SCHEDULE_TYPE_LABEL,
  SETTLEMENT_KIND_LABEL,
  SETTLEMENT_MODE_LABEL,
  SETTLEMENT_TRIGGER_LABEL,
  WEEKDAYS,
  attendanceRuleSentence,
  basisLabel,
  defaultFormValues,
  formFromTerms,
  formProblems,
  money,
  reasonProblem,
  salaryUnit,
  splitDay,
  sumPaise,
  termsFromForm,
  todayStr,
  tomorrowStr,
  weekdayName,
  weeklyScheduledSecondsOf,
  workDateLabel,
  workDateRange,
  type EmploymentFormValues,
  type OvertimeForm,
  type ScheduleRow,
} from './employmentForm';
import {
  ATTENDANCE_STATUSES,
  EMPLOYMENT_MAX_ADJUST_SECONDS,
  EMPLOYMENT_REASON_MAX,
  type AdjustmentPlacement,
  type DayView,
  type EmploymentGet,
  type EmploymentSummary,
  type EmploymentType,
  type PreviewResponse,
  type SettlementListResponse,
  type SettlementRow,
  type TermsOut,
} from './employmentTypes';

/** Query-key root of everything employment for one listener (invalidate it after any write). */
export const employmentKey = (listenerId: string) => ['admin', 'employment', listenerId] as const;

/** GET /admin/listeners/:id/employment — only when the permission is held. */
export function useEmployment(listenerId: string, enabled = true) {
  return useQuery({
    queryKey: employmentKey(listenerId),
    queryFn: async () => (await api.get<EmploymentGet>(`/admin/listeners/${listenerId}/employment`)).data,
    enabled: enabled && !!listenerId,
  });
}

/** Zones offered in the picker; the stored zone is always included. */
function timezoneOptions(current?: string): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const all = intl.supportedValuesOf?.('timeZone') ?? ['Asia/Kolkata', 'UTC'];
  return current && !all.includes(current) ? [current, ...all] : all;
}

type ApiErr = { response?: { status?: number; data?: { code?: string; message?: string; fields?: Record<string, string> } } };

/** The operator-facing message of a failed employment write. */
function writeError(e: unknown, fallback: string): string {
  const data = (e as ApiErr).response?.data;
  switch (data?.code) {
    case 'TERMS_EFFECTIVE_DATE_FROZEN':
      return `${data.message ?? 'Attendance up to that date is already frozen.'} Pick a later effective date, or tick "Backdated correction" if you may adjust attendance.`;
    case 'EMPLOYMENT_EXISTS':
      return 'This listener is already an employee — reload the page.';
    case 'PAYROLL_PERIOD_LOCKED':
      return 'That payroll period is locked; nothing was changed.';
    case 'VALIDATION_ERROR': {
      const f = data.fields ?? {};
      const lines = Object.entries(f).map(([k, v]) => `${k}: ${v}`);
      return lines.length ? `${data.message ?? 'Check the form'} — ${lines.join('; ')}` : (data.message ?? fallback);
    }
    default:
      return errMsg(e, fallback);
  }
}

const EMPLOYMENT_TYPE_OPTIONS: Array<{ value: EmploymentType; label: string }> = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
];

const statusColor: Record<string, string> = { active: 'success', paused: 'warning', ended: 'default' };
const attendanceColor: Record<string, string> = {
  present: 'success',
  partial: 'warning',
  absent: 'error',
  day_off: 'default',
  off_day_worked: 'blue',
  holiday: 'cyan',
  holiday_worked: 'blue',
  paused: 'default',
};
const otColor: Record<string, string> = { none: 'default', auto: 'blue', pending: 'gold', approved: 'success', rejected: 'error' };
/** A day's wallet position / a settlement row's status. */
const settlementColor: Record<string, string> = { none: 'default', pending: 'gold', credited: 'success', partial: 'blue', cancelled: 'default' };
const SETTLEMENT_STATUS_LABEL: Record<string, string> = { none: '—', pending: 'Pending', credited: 'Credited', partial: 'Partial', cancelled: 'Cancelled' };

/** Wallet tag of a day or a settlement; "—" (no tag) when there is nothing to settle. */
function SettlementTag({ status }: { status: string }) {
  if (status === 'none') return <>—</>;
  return <Tag color={settlementColor[status] ?? 'default'}>{SETTLEMENT_STATUS_LABEL[status] ?? plain(status)}</Tag>;
}

/** "Manual · End of work day" */
function settlementSummary(t: { settlementMode: string; settlementTrigger: string }): string {
  return `${SETTLEMENT_MODE_LABEL[t.settlementMode as keyof typeof SETTLEMENT_MODE_LABEL] ?? plain(t.settlementMode)} · ${SETTLEMENT_TRIGGER_LABEL[t.settlementTrigger as keyof typeof SETTLEMENT_TRIGGER_LABEL] ?? plain(t.settlementTrigger)}`;
}

/** "Fixed time window" / "Flexible · 6h 00m a day" */
function scheduleSummary(t: { scheduleType: string; dailyTargetSeconds: number | null }): string {
  const label = SCHEDULE_TYPE_LABEL[t.scheduleType as keyof typeof SCHEDULE_TYPE_LABEL] ?? plain(t.scheduleType);
  return t.scheduleType === 'flexible' && t.dailyTargetSeconds ? `${label} · ${formatDuration(t.dailyTargetSeconds)} a day` : label;
}

// ---------------------------------------------------------------------------

/**
 * Admin → Listener → Employment: a salaried listener's configuration (terms
 * versions), attendance days computed by the server, and the ₹ it produces.
 * Nothing is computed here; every figure comes from the employment API, and
 * ₹ arrives as `null` (shown "—") for admins without `listeners.employment.pay.view`.
 */
export function EmploymentTab({ listenerId, listenerName }: { listenerId: string; listenerName: string }) {
  const { can } = usePermissions();
  const canEdit = can('listeners.employment.edit');
  const employment = useEmployment(listenerId);
  const [enabling, setEnabling] = useState(false);

  if (employment.isLoading) return <Spin />;
  if (employment.isError) {
    return <QueryError error={employment.error} what="employment" onRetry={() => void employment.refetch()} />;
  }
  const data = employment.data;
  if (!data) return null;
  const emp = data.employment;
  const seedTerms = data.latestTerms ?? data.currentTerms ?? null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <StatusCard listenerId={listenerId} data={data} enabling={enabling} setEnabling={setEnabling} canEdit={canEdit} />
      {(emp || enabling) && (
        <TermsEditor
          key={seedTerms?.id ?? 'new'}
          listenerId={listenerId}
          listenerName={listenerName}
          existing={emp ? data : null}
          seed={seedTerms}
          canEdit={canEdit}
          onCreated={() => setEnabling(false)}
          onCancelNew={() => setEnabling(false)}
        />
      )}
      {emp && <VersionHistory listenerId={listenerId} />}
      {emp && <AttendanceSection listenerId={listenerId} employmentId={emp.id} listenerName={listenerName} />}
      {!emp && !enabling && <EmploymentHolidaysCard employmentId={null} from={todayStr()} to={addMonths(todayStr(), 12)} />}
    </Space>
  );
}

function addMonths(day: string, n: number): string {
  const { y, m, d } = splitDay(day);
  return new Date(Date.UTC(y, m - 1 + n, d)).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// (1) Status
// ---------------------------------------------------------------------------

function StatusCard({
  listenerId,
  data,
  enabling,
  setEnabling,
  canEdit,
}: {
  listenerId: string;
  data: EmploymentGet;
  enabling: boolean;
  setEnabling: (v: boolean) => void;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const emp = data.employment;
  const [edit, setEdit] = useState<null | { employmentType: EmploymentType; employeeCode: string; endDate: string; reason: string; ending: boolean }>(null);

  const patch = useMutation({
    mutationFn: async (body: { employmentType?: EmploymentType; employeeCode?: string | null; endDate?: string | null; reason: string }) =>
      (await api.patch<EmploymentGet>(`/admin/listeners/${listenerId}/employment`, body)).data,
    onSuccess: () => {
      message.success('Employment details saved');
      void qc.invalidateQueries({ queryKey: employmentKey(listenerId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'employment', 'badges'] });
      setEdit(null);
    },
    onError: (e: unknown) => message.error(writeError(e, 'Could not save the employment details')),
  });

  const today = data.today;
  const rProblem = edit ? reasonProblem(edit.reason) : null;

  return (
    <Card
      size="small"
      title="Employment status"
      extra={
        <Space>
          <Typography.Text type="secondary">Employee</Typography.Text>
          <Switch
            aria-label="Employee"
            checked={!!emp && emp.status !== 'ended'}
            disabled={!canEdit || (!!emp && emp.status === 'ended')}
            onChange={(on) => {
              if (!emp) setEnabling(on);
              else if (!on) setEdit({ employmentType: emp.employmentType, employeeCode: emp.employeeCode ?? '', endDate: todayStr(), reason: '', ending: true });
            }}
          />
        </Space>
      }
    >
      {!emp ? (
        <Space direction="vertical">
          <Typography.Text>This listener is not an employee. Their earnings come from calls, gifts and referrals only.</Typography.Text>
          {canEdit ? (
            !enabling && (
              <Button type="primary" onClick={() => setEnabling(true)}>
                Enable employment
              </Button>
            )
          ) : (
            <Typography.Text type="secondary">You can view employment but not enable it.</Typography.Text>
          )}
        </Space>
      ) : (
        <>
          <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered>
            <Descriptions.Item label="Type">{plain(emp.employmentType)}</Descriptions.Item>
            <Descriptions.Item label="State">
              <Tag color={statusColor[emp.status] ?? 'default'}>{plain(emp.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Employee code">{emp.employeeCode ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Joining date">{emp.joiningDate}</Descriptions.Item>
            <Descriptions.Item label="End date">{emp.endDate ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Terms versions">{data.versions}</Descriptions.Item>
            {today && (
              <>
                <Descriptions.Item label="Today">
                  <Tag color={attendanceColor[today.status] ?? 'default'}>{plain(today.status)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Worked today">{formatDuration(today.workedSeconds)}</Descriptions.Item>
                <Descriptions.Item label="Scheduled today">{formatDuration(today.scheduledSeconds)}</Descriptions.Item>
              </>
            )}
          </Descriptions>
          {canEdit && (
            <Space style={{ marginTop: 12 }}>
              <Button onClick={() => setEdit({ employmentType: emp.employmentType, employeeCode: emp.employeeCode ?? '', endDate: emp.endDate ?? '', reason: '', ending: false })}>
                Edit details
              </Button>
            </Space>
          )}
        </>
      )}
      <Modal
        title={edit?.ending ? 'End employment' : 'Edit employment details'}
        open={!!edit}
        onCancel={patch.isPending ? undefined : () => setEdit(null)}
        okText={edit?.ending ? 'End employment' : 'Save'}
        okButtonProps={{ danger: !!edit?.ending, disabled: !edit || !!rProblem }}
        confirmLoading={patch.isPending}
        destroyOnHidden
        onOk={() => {
          if (!edit || rProblem) return;
          patch.mutate({
            employmentType: edit.employmentType,
            employeeCode: edit.employeeCode.trim() || null,
            endDate: edit.endDate || null,
            reason: edit.reason.trim(),
          });
        }}
      >
        {edit && (
          <Space direction="vertical" style={{ width: '100%' }}>
            {edit.ending && (
              <Alert type="warning" showIcon message="Attendance stops at the end date; earlier days keep their figures." />
            )}
            <Field label="Type">
              <Select<EmploymentType> value={edit.employmentType} options={EMPLOYMENT_TYPE_OPTIONS} onChange={(v) => setEdit({ ...edit, employmentType: v })} style={{ width: 180 }} />
            </Field>
            <Field label="Employee code">
              <Input value={edit.employeeCode} maxLength={40} onChange={(e) => setEdit({ ...edit, employeeCode: e.target.value })} style={{ width: 220 }} />
            </Field>
            <Field label="End date (blank = none)">
              <Input type="date" aria-label="End date" value={edit.endDate} onChange={(e) => setEdit({ ...edit, endDate: e.target.value })} style={{ width: 180 }} />
            </Field>
            <Field label="Reason">
              <Input.TextArea aria-label="Reason" rows={2} maxLength={EMPLOYMENT_REASON_MAX} showCount value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} />
              {edit.reason && rProblem && <Typography.Text type="danger">{rProblem}</Typography.Text>}
            </Field>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      {children}
      {hint && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Typography.Text>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (2)–(9) Terms editor
// ---------------------------------------------------------------------------

function TermsEditor({
  listenerId,
  listenerName,
  existing,
  seed,
  canEdit,
  onCreated,
  onCancelNew,
}: {
  listenerId: string;
  listenerName: string;
  /** null = enabling a new employee. */
  existing: EmploymentGet | null;
  seed: TermsOut | null;
  canEdit: boolean;
  onCreated: () => void;
  onCancelNew: () => void;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canAdjust = can('listeners.employment.adjust');
  const readOnly = !canEdit;

  const [form, setForm] = useState<EmploymentFormValues>(() => (seed ? formFromTerms(seed) : defaultFormValues()));
  const set = (patch: Partial<EmploymentFormValues>) => setForm((f) => ({ ...f, ...patch }));
  const setOt = (patch: Partial<OvertimeForm>) => setForm((f) => ({ ...f, overtime: { ...f.overtime, ...patch } }));
  const setDay = (weekday: number, patch: Partial<ScheduleRow>) =>
    setForm((f) => ({ ...f, schedule: f.schedule.map((r) => (r.weekday === weekday ? { ...r, ...patch } : r)) }));

  const problems = useMemo(() => formProblems(form), [form]);
  const weekly = weeklyScheduledSecondsOf(form);
  const fixedWindow = form.scheduleType === 'fixed_window';
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(seed ? formFromTerms(seed) : defaultFormValues()), [form, seed]);

  // ----- preview -----
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const runPreview = useMutation({
    mutationFn: async () =>
      (await api.post<PreviewResponse>(`/admin/listeners/${listenerId}/employment/preview`, { terms: termsFromForm(form), weekday: 1, workedSecondsSamples: [] })).data,
    onSuccess: setPreview,
    onError: (e: unknown) => message.error(writeError(e, 'Could not compute the preview')),
  });
  useEffect(() => setPreview(null), [form]);

  // ----- save -----
  const [save, setSave] = useState<null | { effectiveFrom: string; reason: string; isCorrection: boolean; employmentType: EmploymentType; employeeCode: string; endDate: string }>(null);
  const openSave = () =>
    setSave({
      effectiveFrom: existing ? tomorrowStr() : todayStr(),
      reason: '',
      isCorrection: false,
      employmentType: existing?.employment?.employmentType ?? 'full_time',
      employeeCode: existing?.employment?.employeeCode ?? '',
      endDate: '',
    });
  const saveTerms = useMutation({
    mutationFn: async () => {
      if (!save) throw new Error('no save state');
      const terms = termsFromForm(form);
      if (existing) {
        return (
          await api.post<TermsOut>(`/admin/listeners/${listenerId}/employment/terms`, {
            effectiveFrom: save.effectiveFrom,
            isCorrection: save.isCorrection,
            terms,
            reason: save.reason.trim(),
          })
        ).data;
      }
      return (
        await api.post<EmploymentGet>(`/admin/listeners/${listenerId}/employment`, {
          employmentType: save.employmentType,
          employeeCode: save.employeeCode.trim() || null,
          joiningDate: save.effectiveFrom,
          endDate: save.endDate || null,
          terms,
          reason: save.reason.trim(),
        })
      ).data;
    },
    onSuccess: () => {
      message.success(existing ? `New terms saved — effective from ${save?.effectiveFrom}` : `${listenerName} is now an employee`);
      void qc.invalidateQueries({ queryKey: employmentKey(listenerId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'employment', 'badges'] });
      setSave(null);
      if (!existing) onCreated();
    },
    onError: (e: unknown) => message.error(writeError(e, 'Could not save the terms')),
  });
  const saveReasonProblem = save ? reasonProblem(save.reason) : null;
  const saveDateOk = !!save && /^\d{4}-\d{2}-\d{2}$/.test(save.effectiveFrom);

  const numberInput = (value: number | null, onChange: (v: number | null) => void, opts: { min?: number; max?: number; step?: number; width?: number; suffix?: string; label: string }) => (
    <InputNumber
      aria-label={opts.label}
      value={value}
      min={opts.min ?? 0}
      max={opts.max}
      step={opts.step ?? 1}
      disabled={readOnly}
      onChange={(v) => onChange(v === null || v === undefined ? null : Number(v))}
      style={{ width: opts.width ?? 110 }}
      addonAfter={opts.suffix}
    />
  );

  // Fixed window: start / end / break per weekday. Flexible: only which days
  // are working days — the time is the one "Required hours per day" input.
  const fixedWindowColumns: TableColumnsType<ScheduleRow> = [
    {
      title: 'Start',
      width: 140,
      render: (_: unknown, r) => <Input type="time" aria-label={`${weekdayName(r.weekday)} start`} value={r.start} disabled={readOnly || !r.working} onChange={(e) => e.target.value && setDay(r.weekday, { start: e.target.value })} />,
    },
    {
      title: 'End',
      width: 140,
      render: (_: unknown, r) => <Input type="time" aria-label={`${weekdayName(r.weekday)} end`} value={r.end} disabled={readOnly || !r.working} onChange={(e) => e.target.value && setDay(r.weekday, { end: e.target.value })} />,
    },
    {
      title: 'Break (min)',
      width: 130,
      render: (_: unknown, r) => (
        <InputNumber aria-label={`${weekdayName(r.weekday)} break minutes`} value={r.breakMinutes} min={0} max={1439} disabled={readOnly || !r.working} onChange={(v) => setDay(r.weekday, { breakMinutes: Number(v ?? 0) })} style={{ width: 100 }} />
      ),
    },
    { title: 'Net hours', render: (_: unknown, r) => (r.working ? formatDuration(Math.max(0, (((hhmm(r.end) > hhmm(r.start) ? hhmm(r.end) - hhmm(r.start) : 1440 - hhmm(r.start) + hhmm(r.end)) - (r.breakMinutes || 0)) * 60))) : '—') },
  ];
  const flexibleColumns: TableColumnsType<ScheduleRow> = [
    { title: 'Required', render: (_: unknown, r) => (r.working ? formatDuration(Math.round((form.dailyTargetHours ?? 0) * 3600)) : '—') },
  ];
  const scheduleColumns: TableColumnsType<ScheduleRow> = [
    { title: 'Day', width: 110, render: (_: unknown, r) => weekdayName(r.weekday, true) },
    {
      title: 'Working',
      width: 90,
      render: (_: unknown, r) => <Switch aria-label={`${weekdayName(r.weekday)} working`} checked={r.working} disabled={readOnly} onChange={(v) => setDay(r.weekday, { working: v })} />,
    },
    ...(fixedWindow ? fixedWindowColumns : flexibleColumns),
  ];

  const ot = form.overtime;
  const sampleColumns: TableColumnsType<PreviewResponse['samples'][number]> = [
    { title: 'Sample', dataIndex: 'label', render: (l: string) => plain(l) },
    { title: 'Scheduled', render: (_: unknown, s) => formatDuration(s.scheduledSeconds) },
    { title: 'Worked', render: (_: unknown, s) => formatDuration(s.workedSeconds) },
    { title: 'Regular', render: (_: unknown, s) => formatDuration(s.regularSeconds) },
    { title: 'Overtime', render: (_: unknown, s) => formatDuration(s.overtimeSeconds) },
    { title: 'Missing', render: (_: unknown, s) => formatDuration(s.missingSeconds) },
    { title: 'Base', render: (_: unknown, s) => money(s.basePaise) },
    { title: 'OT', render: (_: unknown, s) => money(s.overtimePaise) },
    { title: 'Status', render: (_: unknown, s) => <Tag color={attendanceColor[s.status] ?? 'default'}>{plain(s.status)}</Tag> },
  ];

  return (
    <>
      {!existing && (
        <Alert
          type="info"
          showIcon
          message="Enabling employment"
          description="Set the schedule, work basis, earning rate and rules below, then Save. Employee pay settles into the listener wallet (automatically, or when an admin clicks Credit to Wallet); the listener's calls stop earning normal call pay while employment is active."
        />
      )}
      {existing?.latestTerms && existing.currentTerms && existing.latestTerms.id !== existing.currentTerms.id && (
        <Alert type="info" showIcon message={`Showing version ${existing.latestTerms.versionNo}, effective from ${existing.latestTerms.effectiveFrom} (not in force yet).`} />
      )}

      {/* (2) Schedule */}
      <Card size="small" title="Schedule" extra={<Typography.Text type="secondary">Weekly scheduled: <strong>{formatDuration(weekly)}</strong></Typography.Text>}>
        <Field label="Schedule type">
          <Radio.Group value={form.scheduleType} disabled={readOnly} onChange={(e) => set({ scheduleType: e.target.value as EmploymentFormValues['scheduleType'] })}>
            <Space direction="vertical">
              <Radio value="fixed_window">
                <strong>{SCHEDULE_TYPE_LABEL.fixed_window}</strong> — <Typography.Text type="secondary">only work inside each day&apos;s start–end window counts.</Typography.Text>
              </Radio>
              <Radio value="flexible">
                <strong>{SCHEDULE_TYPE_LABEL.flexible}</strong> — <Typography.Text type="secondary">a required number of hours per working day, any time in the work day.</Typography.Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Field>
        {!fixedWindow && (
          <div style={{ margin: '12px 0' }}>
            <Field label="Required hours per day" hint="The daily target on every working day.">
              {numberInput(form.dailyTargetHours, (v) => set({ dailyTargetHours: v }), { min: 0, max: 24, step: 0.5, label: 'Required hours per day', suffix: 'h' })}
            </Field>
          </div>
        )}
        <Table<ScheduleRow> size="small" rowKey="weekday" columns={scheduleColumns} dataSource={form.schedule} pagination={false} style={{ marginTop: 12 }} />
        <Space wrap style={{ marginTop: 12 }}>
          <Field label="Work-day start" hint={fixedWindow ? 'Shifts crossing midnight must fit inside one work day.' : 'When the employee’s work day begins (00:00 normally).'}>
            <Input type="time" aria-label="Work-day start" value={form.dayStart} disabled={readOnly} onChange={(e) => e.target.value && set({ dayStart: e.target.value })} style={{ width: 140 }} />
          </Field>
          <Field label="Week starts on">
            <Select<number> aria-label="Week start" value={form.weekStartDay} disabled={readOnly} options={WEEKDAYS.map((w) => ({ value: w.weekday, label: w.long }))} onChange={(v) => set({ weekStartDay: v })} style={{ width: 150 }} />
          </Field>
          <Field label="Timezone">
            <Select<string> aria-label="Timezone" showSearch value={form.timezone} disabled={readOnly} options={timezoneOptions(form.timezone).map((z) => ({ value: z, label: z }))} onChange={(v) => set({ timezone: v })} style={{ width: 220 }} />
          </Field>
          <Field label="State">
            <Select<EmploymentFormValues['state']> aria-label="Terms state" value={form.state} disabled={readOnly} options={[{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused (no attendance, no pay)' }]} onChange={(v) => set({ state: v })} style={{ width: 240 }} />
          </Field>
        </Space>
      </Card>

      {/* (3) Work time basis */}
      <Card size="small" title="Work time basis">
        <Radio.Group value={form.workTimeBasis} disabled={readOnly} onChange={(e) => set({ workTimeBasis: e.target.value as EmploymentFormValues['workTimeBasis'] })}>
          <Space direction="vertical">
            {BASIS_OPTIONS.filter((b) => !b.advanced).map((b) => (
              <Radio key={b.value} value={b.value}>
                <strong>{b.label}</strong> — <Typography.Text type="secondary">{b.hint}</Typography.Text>
              </Radio>
            ))}
            <Typography.Text type="secondary" strong style={{ fontSize: 12, marginTop: 4 }}>
              Advanced — heartbeat-based
            </Typography.Text>
            {BASIS_OPTIONS.filter((b) => b.advanced).map((b) => (
              <Radio key={b.value} value={b.value}>
                <strong>{b.label}</strong> — <Typography.Text type="secondary">{b.hint}</Typography.Text>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        <div style={{ marginTop: 12 }}>
          <Field label="Break" hint="Auto-deduct takes the scheduled break off the presence once the day's presence covers the shift.">
            <Select<EmploymentFormValues['breakMode']> aria-label="Break mode" value={form.breakMode} disabled={readOnly} options={[{ value: 'auto_deduct', label: 'Deduct the scheduled break automatically' }, { value: 'none', label: 'No break deduction' }]} onChange={(v) => set({ breakMode: v })} style={{ width: 320 }} />
          </Field>
        </div>
      </Card>

      {/* (4) Earning rate */}
      <Card size="small" title="Earning rate">
        <Space wrap align="start">
          <Field label="Rate model">
            <Select<EmploymentFormValues['salaryModel']> aria-label="Salary frequency" value={form.salaryModel} disabled={readOnly} options={(Object.keys(SALARY_MODEL_LABEL) as Array<keyof typeof SALARY_MODEL_LABEL>).map((k) => ({ value: k, label: k === 'hourly' ? 'Hourly (₹ per hour)' : SALARY_MODEL_LABEL[k] }))} onChange={(v) => set({ salaryModel: v, ...(v === 'hourly' ? { salaryAttendanceMode: 'prorated' as const } : {}) })} style={{ width: 190 }} />
          </Field>
          <Field label={`Amount (${salaryUnit(form.salaryModel)})`}>
            <Input aria-label="Salary amount" prefix="₹" suffix={form.salaryModel === 'hourly' ? 'per hour' : undefined} inputMode="decimal" placeholder="0.00" value={form.salaryRupees} disabled={readOnly} onChange={(e) => set({ salaryRupees: e.target.value })} style={{ width: 200 }} />
          </Field>
          {form.salaryModel !== 'hourly' && (
            <Field label="Attendance rule">
              <Select<EmploymentFormValues['salaryAttendanceMode']> aria-label="Attendance rule" value={form.salaryAttendanceMode} disabled={readOnly} options={[{ value: 'prorated', label: 'Prorated' }, { value: 'fixed', label: 'Fixed' }]} onChange={(v) => set({ salaryAttendanceMode: v })} style={{ width: 140 }} />
            </Field>
          )}
          <Field label="Present tolerance (minutes)" hint="Lateness up to this still counts as Present.">
            {numberInput(form.presentToleranceMinutes, (v) => set({ presentToleranceMinutes: v ?? 0 }), { min: 0, max: 60, label: 'Present tolerance minutes' })}
          </Field>
        </Space>
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 8 }}>
          {attendanceRuleSentence(form.salaryModel, form.salaryAttendanceMode)}
        </Typography.Paragraph>
        <Space align="start">
          <Switch aria-label="Minimum target completion required" checked={form.minimumCompletionRequired} disabled={readOnly} onChange={(v) => set({ minimumCompletionRequired: v })} />
          <span>
            <strong>Minimum target completion required</strong>
            <br />
            <Typography.Text type="secondary">No regular pay on a working day unless the daily target is met.</Typography.Text>
          </span>
        </Space>
      </Card>

      {/* (5) Overtime */}
      <Card
        size="small"
        title="Overtime"
        extra={<Switch aria-label="Overtime enabled" checked={ot.enabled} disabled={readOnly} onChange={(v) => setOt({ enabled: v })} />}
      >
        {fixedWindow && (
          <Space align="start" style={{ marginBottom: ot.enabled ? 16 : 0, display: 'flex' }}>
            <Switch aria-label="Count work outside the window" checked={ot.outsideWindowCounts} disabled={readOnly} onChange={(v) => setOt({ outsideWindowCounts: v })} />
            <span>
              <strong>Count work outside the window</strong>
              <br />
              <Typography.Text type="secondary">Presence before the start or after the end counts as worked time — it can become overtime. Off: it is recorded but never paid.</Typography.Text>
            </span>
          </Space>
        )}
        {!ot.enabled ? (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: fixedWindow ? 12 : 0 }}>Off — time beyond the schedule is reported as unpaid extra time.</Typography.Text>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap align="start">
              <Field label="Trigger">
                <Select<OvertimeForm['trigger']> aria-label="Overtime trigger" value={ot.trigger} disabled={readOnly} options={[{ value: 'after_scheduled_hours', label: 'After the scheduled hours' }, { value: 'after_shift_end', label: 'After the shift end time' }, { value: 'daily_threshold', label: 'After a daily threshold' }]} onChange={(v) => setOt({ trigger: v })} style={{ width: 240 }} />
              </Field>
              {ot.trigger === 'daily_threshold' && (
                <Field label="Daily threshold (hours)" hint="At least the longest scheduled day.">
                  {numberInput(ot.thresholdHours, (v) => setOt({ thresholdHours: v }), { min: 0, max: 24, step: 0.25, label: 'Daily threshold hours' })}
                </Field>
              )}
              <Field label="Pay method">
                <Select<OvertimeForm['payMethod']> aria-label="Overtime pay method" value={ot.payMethod} disabled={readOnly} options={[{ value: 'same_rate', label: 'Same hourly rate' }, { value: 'multiplier', label: 'Multiplier of the hourly rate' }, { value: 'fixed_rate', label: 'Fixed ₹ per hour' }]} onChange={(v) => setOt({ payMethod: v })} style={{ width: 240 }} />
              </Field>
              {ot.payMethod === 'same_rate' ? null : ot.payMethod === 'fixed_rate' ? (
                <Field label="Rate (₹ per hour)">
                  <Input aria-label="Overtime rate" prefix="₹" inputMode="decimal" placeholder="0.00" value={ot.rateRupees} disabled={readOnly} onChange={(e) => setOt({ rateRupees: e.target.value })} style={{ width: 150 }} />
                </Field>
              ) : (
                <>
                  <Field label="Multiplier (×)">
                    {numberInput(ot.multiplier, (v) => setOt({ multiplier: v ?? 0 }), { min: 0.1, max: 10, step: 0.1, label: 'Overtime multiplier' })}
                  </Field>
                  <Field label="Hourly rate divisor" hint="How the salary becomes an hourly rate.">
                    <Select<OvertimeForm['divisorMode']> aria-label="Divisor mode" value={ot.divisorMode} disabled={readOnly} options={[{ value: 'period_scheduled_hours', label: "The period's scheduled hours" }, { value: 'fixed_hours', label: 'Fixed hours per period' }]} onChange={(v) => setOt({ divisorMode: v })} style={{ width: 230 }} />
                  </Field>
                  {ot.divisorMode === 'fixed_hours' && (
                    <Field label="Fixed hours">{numberInput(ot.divisorHours, (v) => setOt({ divisorHours: v }), { min: 1, max: 744, label: 'Divisor hours' })}</Field>
                  )}
                </>
              )}
            </Space>
            <Space wrap align="start">
              <Field label="Minimum (minutes)" hint="Less than this is not overtime.">{numberInput(ot.minMinutes, (v) => setOt({ minMinutes: v ?? 0 }), { min: 0, max: 1440, label: 'Overtime minimum minutes' })}</Field>
              <Field label="Round to (minutes)" hint="0 = no rounding.">{numberInput(ot.roundMinutes, (v) => setOt({ roundMinutes: v ?? 0 }), { min: 0, max: 240, label: 'Overtime rounding minutes' })}</Field>
              <Field label="Round direction">
                <Select<OvertimeForm['roundDirection']> aria-label="Round direction" value={ot.roundDirection} disabled={readOnly} options={[{ value: 'down', label: 'Down' }, { value: 'nearest', label: 'Nearest' }, { value: 'up', label: 'Up' }]} onChange={(v) => setOt({ roundDirection: v })} style={{ width: 120 }} />
              </Field>
              <Field label="Daily cap (hours)" hint="Blank = no cap.">{numberInput(ot.dailyCapHours, (v) => setOt({ dailyCapHours: v }), { min: 0, max: 24, step: 0.5, label: 'Daily overtime cap hours' })}</Field>
              <Field label="Period cap (hours)" hint="Blank = no cap.">{numberInput(ot.periodCapHours, (v) => setOt({ periodCapHours: v }), { min: 0, max: 744, step: 0.5, label: 'Period overtime cap hours' })}</Field>
              <Field label="Period cap unit">
                <Select<OvertimeForm['periodCapUnit']> aria-label="Period cap unit" value={ot.periodCapUnit} disabled={readOnly} options={[{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }]} onChange={(v) => setOt({ periodCapUnit: v })} style={{ width: 110 }} />
              </Field>
            </Space>
            <Space wrap size="large">
              <Checkbox checked={ot.approvalRequired} disabled={readOnly} onChange={(e) => setOt({ approvalRequired: e.target.checked })}>
                Overtime needs admin approval before it is paid
              </Checkbox>
              <Checkbox checked={ot.manualAdjustAllowed} disabled={readOnly} onChange={(e) => setOt({ manualAdjustAllowed: e.target.checked })}>
                Admins may adjust worked time by hand
              </Checkbox>
            </Space>
          </Space>
        )}
      </Card>

      {/* (6) Off days & holidays */}
      <Card size="small" title="Off days & holidays">
        <Space wrap align="start">
          <Field label="Work on an off day">
            <Select<EmploymentFormValues['offDayRule']> aria-label="Off-day rule" value={form.offDayRule} disabled={readOnly} options={OFF_DAY_OPTIONS} onChange={(v) => set({ offDayRule: v })} style={{ width: 230 }} />
          </Field>
          <Field label="Holiday pay">
            <Select<EmploymentFormValues['holidayPay']> aria-label="Holiday pay" value={form.holidayPay} disabled={readOnly} options={[{ value: 'paid', label: 'Paid (counts as a worked day)' }, { value: 'unpaid', label: 'Unpaid' }]} onChange={(v) => set({ holidayPay: v })} style={{ width: 230 }} />
          </Field>
          <Field label="Work on a holiday">
            <Select<EmploymentFormValues['holidayWorkRule']> aria-label="Holiday work rule" value={form.holidayWorkRule} disabled={readOnly} options={OFF_DAY_OPTIONS} onChange={(v) => set({ holidayWorkRule: v })} style={{ width: 230 }} />
          </Field>
        </Space>
      </Card>

      {/* (7) Wallet settlement */}
      <Card size="small" title="Wallet settlement">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Employee pay is settled into the listener&apos;s wallet. Each work day produces a settlement; this decides who credits it and when.
        </Typography.Paragraph>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Field label="Settlement mode">
            <Radio.Group value={form.settlementMode} disabled={readOnly} onChange={(e) => set({ settlementMode: e.target.value as EmploymentFormValues['settlementMode'] })}>
              <Space direction="vertical">
                <Radio value="automatic">
                  <strong>{SETTLEMENT_MODE_LABEL.automatic}</strong> — <Typography.Text type="secondary">the worker credits the wallet after each work day.</Typography.Text>
                </Radio>
                <Radio value="manual">
                  <strong>{SETTLEMENT_MODE_LABEL.manual}</strong> — <Typography.Text type="secondary">an admin reviews the day and clicks Credit to Wallet.</Typography.Text>
                </Radio>
              </Space>
            </Radio.Group>
          </Field>
          <Field label="Settlement trigger">
            <Radio.Group value={form.settlementTrigger} disabled={readOnly} onChange={(e) => set({ settlementTrigger: e.target.value as EmploymentFormValues['settlementTrigger'] })}>
              <Space direction="vertical">
                <Radio value="end_of_day">
                  <strong>{SETTLEMENT_TRIGGER_LABEL.end_of_day}</strong> — <Typography.Text type="secondary">one settlement once the day is frozen.</Typography.Text>
                </Radio>
                <Radio value="target_completed">
                  <strong>{SETTLEMENT_TRIGGER_LABEL.target_completed}</strong> — <Typography.Text type="secondary">regular pay is released as soon as the target is met; overtime at the end of the day.</Typography.Text>
                </Radio>
              </Space>
            </Radio.Group>
          </Field>
        </Space>
      </Card>

      {/* (8) Preview + (9) Save */}
      <Card size="small" title="Preview & save">
        {problems.length > 0 && <Alert type="warning" showIcon message="Fix before saving" description={<ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>} style={{ marginBottom: 12 }} />}
        <Space wrap>
          <Button onClick={() => runPreview.mutate()} loading={runPreview.isPending} disabled={problems.length > 0}>
            Preview calculation
          </Button>
          {canEdit && (
            <Button type="primary" onClick={openSave} disabled={problems.length > 0 || (!!existing && !dirty)}>
              {existing ? 'Save as new version' : 'Save & enable employment'}
            </Button>
          )}
          {!existing && <Button onClick={onCancelNew}>Cancel</Button>}
          {existing && dirty && <Typography.Text type="secondary">Unsaved changes</Typography.Text>}
        </Space>
        {preview && (
          <div style={{ marginTop: 12 }} data-testid="employment-preview">
            <Space wrap style={{ marginBottom: 8 }}>
              <Typography.Text type="secondary">Sample {preview.sampleDate} (Monday)</Typography.Text>
              <Typography.Text type="secondary">Weekly scheduled {formatDuration(preview.weeklyScheduledSeconds)}</Typography.Text>
              <Typography.Text type="secondary">Period scheduled {formatDuration(preview.periodScheduledSeconds)}</Typography.Text>
              <Typography.Text type="secondary">Derived hourly {money(preview.derivedHourlyPaise)}</Typography.Text>
              <Typography.Text type="secondary">Schedule: {scheduleSummary({ scheduleType: form.scheduleType, dailyTargetSeconds: Math.round((form.dailyTargetHours ?? 0) * 3600) })}</Typography.Text>
              <Typography.Text type="secondary">Settlement: {settlementSummary(form)}</Typography.Text>
            </Space>
            <Table size="small" rowKey="label" columns={sampleColumns} dataSource={preview.samples} pagination={false} scroll={{ x: 'max-content' }} />
          </div>
        )}
      </Card>

      <Modal
        title={existing ? 'Save new terms version' : `Enable employment for ${listenerName}`}
        open={!!save}
        onCancel={saveTerms.isPending ? undefined : () => setSave(null)}
        okText={existing ? 'Save version' : 'Enable employment'}
        okButtonProps={{ disabled: !save || !!saveReasonProblem || !saveDateOk }}
        confirmLoading={saveTerms.isPending}
        destroyOnHidden
        onOk={() => saveTerms.mutate()}
      >
        {save && (
          <Space direction="vertical" style={{ width: '100%' }}>
            {!existing && (
              <>
                <Field label="Type">
                  <Select<EmploymentType> aria-label="Employment type" value={save.employmentType} options={EMPLOYMENT_TYPE_OPTIONS} onChange={(v) => setSave({ ...save, employmentType: v })} style={{ width: 180 }} />
                </Field>
                <Field label="Employee code (optional)">
                  <Input aria-label="Employee code" value={save.employeeCode} maxLength={40} onChange={(e) => setSave({ ...save, employeeCode: e.target.value })} style={{ width: 220 }} />
                </Field>
              </>
            )}
            <Field label={existing ? 'Effective from' : 'Joining date'} hint={existing ? 'Days before this keep the previous version. Frozen days need a backdated correction.' : undefined}>
              <Input type="date" aria-label={existing ? 'Effective from' : 'Joining date'} value={save.effectiveFrom} onChange={(e) => setSave({ ...save, effectiveFrom: e.target.value })} style={{ width: 180 }} />
            </Field>
            {!existing && (
              <Field label="End date (optional)">
                <Input type="date" aria-label="End date" value={save.endDate} onChange={(e) => setSave({ ...save, endDate: e.target.value })} style={{ width: 180 }} />
              </Field>
            )}
            {existing && canAdjust && (
              <Checkbox checked={save.isCorrection} onChange={(e) => setSave({ ...save, isCorrection: e.target.checked })}>
                Backdated correction — re-settle frozen days from the effective date
              </Checkbox>
            )}
            <Field label="Reason">
              <Input.TextArea aria-label="Save reason" rows={2} maxLength={EMPLOYMENT_REASON_MAX} showCount value={save.reason} onChange={(e) => setSave({ ...save, reason: e.target.value })} placeholder="Why these terms? Kept in the version history." />
              {save.reason && saveReasonProblem && <Typography.Text type="danger">{saveReasonProblem}</Typography.Text>}
            </Field>
          </Space>
        )}
      </Modal>
    </>
  );
}

const OFF_DAY_OPTIONS = [
  { value: 'unpaid' as const, label: 'Unpaid (reported only)' },
  { value: 'overtime' as const, label: 'Paid as overtime' },
  { value: 'regular' as const, label: 'Paid at the regular rate' },
];

function hhmm(v: string): number {
  const [h = 0, m = 0] = v.split(':').map((x) => Number.parseInt(x, 10));
  return h * 60 + m;
}

// ---------------------------------------------------------------------------
// (10) Version history
// ---------------------------------------------------------------------------

function VersionHistory({ listenerId }: { listenerId: string }) {
  const versions = useQuery({
    queryKey: [...employmentKey(listenerId), 'terms'],
    queryFn: async () => (await api.get<{ items: TermsOut[] }>(`/admin/listeners/${listenerId}/employment/terms`)).data,
  });
  const columns: TableColumnsType<TermsOut> = [
    { title: 'Version', dataIndex: 'versionNo', width: 80 },
    { title: 'Effective from', dataIndex: 'effectiveFrom', width: 120 },
    { title: 'State', dataIndex: 'state', width: 90, render: (s: string) => <Tag color={statusColor[s] ?? 'default'}>{plain(s)}</Tag> },
    { title: 'Salary', width: 130, render: (_: unknown, t) => `${money(t.salaryAmountPaise)}${t.salaryAmountPaise === null ? '' : ` / ${t.salaryModel.replace('ly', '')}`}` },
    { title: 'Model', width: 150, render: (_: unknown, t) => `${SALARY_MODEL_LABEL[t.salaryModel] ?? t.salaryModel} · ${plain(t.salaryAttendanceMode)}` },
    { title: 'Schedule', width: 150, render: (_: unknown, t) => scheduleSummary(t) },
    { title: 'Basis', width: 120, render: (_: unknown, t) => basisLabel(t.workTimeBasis) },
    { title: 'Settlement', width: 180, render: (_: unknown, t) => settlementSummary(t) },
    { title: 'Changed by', dataIndex: 'createdBy', width: 110, render: (id: string) => <Typography.Text copyable={{ text: id }}>{id.slice(0, 8)}</Typography.Text> },
    { title: 'At', dataIndex: 'createdAt', width: 170, render: (iso: string) => new Date(iso).toLocaleString() },
    { title: 'Reason', dataIndex: 'changeReason' },
    { title: '', width: 100, render: (_: unknown, t) => (t.isCorrection ? <Tag color="purple">Correction</Tag> : null) },
  ];
  return (
    <Card size="small" title="Version history">
      {versions.isError ? (
        <QueryError error={versions.error} what="terms versions" onRetry={() => void versions.refetch()} />
      ) : (
        <Table<TermsOut> size="small" rowKey="id" loading={versions.isLoading} columns={columns} dataSource={versions.data?.items ?? []} pagination={false} scroll={{ x: 'max-content' }} />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// (11) Attendance + (12) Earnings summary
// ---------------------------------------------------------------------------

type DayAction = 'adjust' | 'approve' | 'reject' | 'recompute';

function AttendanceSection({ listenerId, employmentId, listenerName }: { listenerId: string; employmentId: string; listenerName: string }) {
  const { preset, setPreset, custom, setCustom, range } = useStatsRange('thisMonth');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const dates = useMemo(() => (range ? workDateRange(range) : null), [range]);
  const [action, setAction] = useState<null | { kind: DayAction; day: DayView }>(null);

  const attendance = useQuery({
    queryKey: [...employmentKey(listenerId), 'attendance', dates?.from, dates?.to, status ?? ''],
    queryFn: async () =>
      (await api.get<{ from: string; to: string; items: DayView[] }>(`/admin/listeners/${listenerId}/employment/attendance`, { params: { ...dates, status } })).data,
    enabled: !!dates,
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: [...employmentKey(listenerId), 'summary', dates?.from, dates?.to],
    queryFn: async () => (await api.get<EmploymentSummary>(`/admin/listeners/${listenerId}/employment/summary`, { params: dates })).data,
    enabled: !!dates,
    placeholderData: keepPreviousData,
  });
  // The same overview query the page uses — call earnings for the same range.
  const overview = useQuery({
    queryKey: ['admin', 'listener-overview', listenerId, range],
    queryFn: async () => (await api.get<ListenerOverview>(`/admin/listeners/${listenerId}/overview`, { params: range as StatsRangeParams })).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => attendance.data?.items ?? [], [attendance.data]);
  const totals = useMemo(() => {
    const n = (f: (d: DayView) => number) => rows.reduce((s, d) => s + f(d), 0);
    return {
      scheduled: n((d) => d.scheduledSeconds),
      presence: n((d) => d.presenceSeconds),
      brk: n((d) => d.breakDeductedSeconds),
      worked: n((d) => d.workedSeconds),
      regular: n((d) => d.regularSeconds),
      overtime: n((d) => d.overtimeSeconds),
      unpaid: n((d) => d.unpaidExtraSeconds),
      outside: n((d) => d.outsideWindowSeconds ?? 0),
      missing: n((d) => d.missingSeconds),
      base: sumPaise(...rows.map((d) => d.basePaise)),
      ot: sumPaise(...rows.map((d) => d.overtimePaise)),
    };
  }, [rows]);

  const columns: TableColumnsType<DayView> = [
    { title: 'Date', dataIndex: 'workDate', fixed: 'left', width: 110, render: (d: string, r) => <Space size={4}>{d}{!r.frozen && <Tooltip title="Today — still counting"><Tag color="processing">live</Tag></Tooltip>}</Space> },
    { title: 'Type', dataIndex: 'dayType', width: 90, render: (t: string) => plain(t) },
    { title: 'Scheduled', width: 100, render: (_: unknown, d) => formatDuration(d.scheduledSeconds) },
    { title: 'Presence', width: 100, render: (_: unknown, d) => formatDuration(d.presenceSeconds) },
    { title: 'Break', width: 90, render: (_: unknown, d) => formatDuration(d.breakDeductedSeconds) },
    { title: 'Worked', width: 100, render: (_: unknown, d) => <strong>{formatDuration(d.workedSeconds)}</strong> },
    { title: 'Regular', width: 100, render: (_: unknown, d) => formatDuration(d.regularSeconds) },
    {
      title: 'Overtime',
      width: 150,
      render: (_: unknown, d) => (
        <Space size={4}>
          {formatDuration(d.overtimeSeconds)}
          {d.overtimeStatus !== 'none' && <Tag color={otColor[d.overtimeStatus] ?? 'default'}>{OT_STATUS_LABEL[d.overtimeStatus] ?? plain(d.overtimeStatus)}</Tag>}
        </Space>
      ),
    },
    { title: 'Unpaid extra', width: 110, render: (_: unknown, d) => formatDuration(d.unpaidExtraSeconds) },
    {
      title: <Tooltip title="Presence before the shift start or after its end (fixed-window schedules). Paid only when “Count work outside the window” is on.">Outside window</Tooltip>,
      width: 120,
      render: (_: unknown, d) => (d.outsideWindowSeconds > 0 ? formatDuration(d.outsideWindowSeconds) : '—'),
    },
    { title: 'Missing', width: 100, render: (_: unknown, d) => formatDuration(d.missingSeconds) },
    { title: 'Base ₹', width: 110, align: 'right', render: (_: unknown, d) => money(d.basePaise) },
    { title: 'OT ₹', width: 110, align: 'right', render: (_: unknown, d) => money(d.overtimePaise) },
    { title: 'Total ₹', width: 110, align: 'right', render: (_: unknown, d) => <strong>{money(sumPaise(d.basePaise, d.overtimePaise))}</strong> },
    {
      title: <Tooltip title="Whether the day's pay has reached the listener wallet: Pending (waiting for a credit), Credited, or Partial (some credited, some pending).">Wallet</Tooltip>,
      width: 100,
      render: (_: unknown, d) => <SettlementTag status={d.settlementStatus ?? 'none'} />,
    },
    { title: 'Status', dataIndex: 'status', width: 130, render: (s: string) => <Tag color={attendanceColor[s] ?? 'default'}>{plain(s)}</Tag> },
    {
      title: 'Actions',
      fixed: 'right',
      width: 230,
      render: (_: unknown, d) => (
        <Can permission="listeners.employment.adjust">
          <Space size={4} wrap>
            {d.frozen && <Button size="small" onClick={() => setAction({ kind: 'adjust', day: d })}>Adjust</Button>}
            {d.overtimeStatus === 'pending' && <Button size="small" type="primary" onClick={() => setAction({ kind: 'approve', day: d })}>Approve OT</Button>}
            {d.overtimeStatus === 'pending' && <Button size="small" danger onClick={() => setAction({ kind: 'reject', day: d })}>Reject OT</Button>}
            {d.frozen && <Button size="small" icon={<ReloadOutlined />} onClick={() => setAction({ kind: 'recompute', day: d })}>Recompute</Button>}
          </Space>
        </Can>
      ),
    },
  ];

  const s = summary.data;
  const callEarnings = overview.data?.money?.callEarningsPaise ?? null;
  const combined = s ? sumPaise(s.employmentTotalPaise, callEarnings) : null;

  return (
    <>
      <Card
        size="small"
        title="Earnings summary"
        extra={<StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} custom={custom} />}
      >
        {summary.isError ? (
          <QueryError error={summary.error} what="employment summary" onRetry={() => void summary.refetch()} />
        ) : (
          <Row gutter={[16, 16]}>
            <Stat title="Base" value={money(s?.basePaise)} hint="Regular time at the salary rate (prorated or fixed)." />
            <Stat title="Overtime" value={money(s?.overtimePaise)} hint="Approved / automatic overtime." />
            <Stat title="Pending OT" value={money(s?.pendingOvertimePaise)} hint={`Waiting for approval — ${s?.pendingOvertimeDays ?? 0} day(s).`} />
            <Stat title="Employment total" value={<strong>{money(s?.employmentTotalPaise)}</strong>} hint="Base + overtime. Pending overtime is not included." />
            <Stat title="Credited to wallet" value={money(s?.settledPaise)} hint="Settlements already credited to the listener wallet in this range." />
            <Stat title="Pending wallet credit" value={money(s?.pendingSettlementPaise)} hint="Settlements waiting for a wallet credit (manual mode, or the worker's next run)." />
            <Stat title="Worked" value={formatDuration(s?.workedSeconds)} />
            <Stat title="Scheduled" value={formatDuration(s?.scheduledSeconds)} />
            <Stat title="Missing" value={formatDuration(s?.missingSeconds)} />
            <Stat title="Days" value={s?.days ?? '—'} />
          </Row>
        )}
        <Typography.Title level={5} style={{ marginTop: 16 }}>
          Listener call earnings
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          From the ledger, for the same range — separate from the employee pay above. Employee calls do not earn normal call pay while employment is active.
        </Typography.Paragraph>
        <Row gutter={[16, 16]}>
          <Stat title="Call earnings" value={money(callEarnings)} />
          <Stat title="Combined display total" value={<strong>{money(combined)}</strong>} hint="Employment total + call earnings. For display only — not a wallet balance." />
        </Row>
      </Card>

      <Card
        size="small"
        title="Attendance"
        extra={
          <Space wrap>
            <Select<string>
              allowClear
              placeholder="Any status"
              aria-label="Attendance status"
              value={status}
              onChange={(v) => setStatus(v || undefined)}
              options={ATTENDANCE_STATUSES.map((st) => ({ value: st, label: plain(st) }))}
              style={{ minWidth: 170 }}
            />
            <Button
              icon={<DownloadOutlined />}
              disabled={rows.length === 0}
              onClick={() => downloadText(`attendance-${listenerId.slice(0, 8)}-${dates?.from}-${dates?.to}.csv`, attendanceCsv(rows))}
            >
              Export CSV
            </Button>
          </Space>
        }
      >
        {attendance.isError ? (
          <QueryError error={attendance.error} what="attendance" onRetry={() => void attendance.refetch()} />
        ) : (
          <Table<DayView>
            size="small"
            rowKey="workDate"
            loading={attendance.isLoading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No attendance days in this range' }}
            summary={() =>
              rows.length > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>{rows.length} days</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{formatDuration(totals.scheduled)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{formatDuration(totals.presence)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>{formatDuration(totals.brk)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5}><strong>{formatDuration(totals.worked)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>{formatDuration(totals.regular)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>{formatDuration(totals.overtime)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>{formatDuration(totals.unpaid)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>{totals.outside > 0 ? formatDuration(totals.outside) : '—'}</Table.Summary.Cell>
                  <Table.Summary.Cell index={10}>{formatDuration(totals.missing)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={11} align="right">{money(totals.base)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={12} align="right">{money(totals.ot)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={13} align="right"><strong>{money(sumPaise(totals.base, totals.ot))}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={14} />
                  <Table.Summary.Cell index={15} />
                  <Table.Summary.Cell index={16} />
                </Table.Summary.Row>
              ) : null
            }
          />
        )}
      </Card>

      {dates && <SettlementHistoryCard listenerId={listenerId} listenerName={listenerName} from={dates.from} to={dates.to} />}

      {dates && <EmploymentHolidaysCard employmentId={employmentId} from={dates.from} to={dates.to} />}

      {action && <DayActionModal listenerId={listenerId} kind={action.kind} day={action.day} onClose={() => setAction(null)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// (13) Settlement history — employee pay → wallet
// ---------------------------------------------------------------------------

const SETTLEMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'credited', label: 'Credited' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Every settlement of this employee in the range: what each work day produced,
 * whether it reached the wallet, and — in manual mode — the Credit to Wallet
 * action for admins holding `listeners.employment.credit_wallet`.
 */
function SettlementHistoryCard({ listenerId, listenerName, from, to }: { listenerId: string; listenerName: string; from: string; to: string }) {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const settlements = useQuery({
    queryKey: [...employmentKey(listenerId), 'settlements', from, to, status ?? ''],
    queryFn: async () =>
      (await api.get<SettlementListResponse>(`/admin/listeners/${listenerId}/employment/settlements`, { params: { from, to, status, pageSize: 100 } })).data,
    placeholderData: keepPreviousData,
  });
  const columns: TableColumnsType<SettlementRow> = [
    { title: 'Date', dataIndex: 'workDate', width: 120, render: (d: string) => workDateLabel(d) },
    { title: 'Kind', dataIndex: 'kind', width: 110, render: (k: string) => SETTLEMENT_KIND_LABEL[k as keyof typeof SETTLEMENT_KIND_LABEL] ?? plain(k) },
    { title: 'Amount', width: 120, align: 'right', render: (_: unknown, r) => <strong>{money(r.amountPaise)}</strong> },
    { title: 'Status', dataIndex: 'status', width: 110, render: (st: string) => <SettlementTag status={st} /> },
    { title: 'Mode', dataIndex: 'mode', width: 100, render: (m: string) => SETTLEMENT_MODE_LABEL[m as keyof typeof SETTLEMENT_MODE_LABEL] ?? plain(m) },
    { title: 'Reason', dataIndex: 'reason', render: (r: string | null) => r ?? '—' },
    {
      title: 'Credited at / by',
      width: 200,
      render: (_: unknown, r) =>
        r.creditedAt ? (
          <Space size={4} wrap>
            {new Date(r.creditedAt).toLocaleString()}
            {r.creditedBy ? <Typography.Text type="secondary" copyable={{ text: r.creditedBy }}>{r.creditedBy.slice(0, 8)}</Typography.Text> : <Typography.Text type="secondary">worker</Typography.Text>}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Wallet txn id',
      width: 150,
      render: (_: unknown, r) => (r.walletTransactionId ? <Typography.Text copyable={{ text: r.walletTransactionId }}>{r.walletTransactionId.slice(0, 8)}…</Typography.Text> : '—'),
    },
    {
      title: '',
      width: 160,
      render: (_: unknown, r) => <CreditSettlementButton settlement={r} listenerId={listenerId} listenerName={listenerName} />,
    },
  ];
  const total = settlements.data?.total ?? 0;
  return (
    <Card
      size="small"
      title="Settlement history"
      extra={
        <Select<string>
          allowClear
          placeholder="Any status"
          aria-label="Settlement status"
          value={status}
          onChange={(v) => setStatus(v || undefined)}
          options={SETTLEMENT_STATUS_OPTIONS}
          style={{ minWidth: 150 }}
        />
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Employee pay that goes to the listener wallet, one row per settlement. Pending rows in manual mode wait for Credit to Wallet.
        {total > 100 ? ` Showing the latest 100 of ${total}.` : ''}
      </Typography.Paragraph>
      {settlements.isError ? (
        <QueryError error={settlements.error} what="settlement history" onRetry={() => void settlements.refetch()} />
      ) : (
        <Table<SettlementRow>
          size="small"
          rowKey="id"
          loading={settlements.isLoading}
          columns={columns}
          dataSource={settlements.data?.items ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No settlements in this range' }}
        />
      )}
    </Card>
  );
}

function Stat({ title, value, hint }: { title: string; value: ReactNode; hint?: string }) {
  return (
    <Col xs={12} md={6}>
      <Statistic title={hint ? <Tooltip title={hint}>{title}</Tooltip> : title} valueRender={() => value} />
    </Col>
  );
}

const ACTION_TITLE: Record<DayAction, string> = {
  adjust: 'Adjust worked time',
  approve: 'Approve overtime',
  reject: 'Reject overtime',
  recompute: 'Recompute the day',
};

/**
 * One audited post-freeze operation on a day. Two steps (form → confirm), a
 * reason, and ONE Idempotency-Key per dialog so a retry after a timeout
 * replays instead of applying twice.
 */
function DayActionModal({ listenerId, kind, day, onClose }: { listenerId: string; kind: DayAction; day: DayView; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [deltaMinutes, setDeltaMinutes] = useState<number | null>(null);
  const [placement, setPlacement] = useState<AdjustmentPlacement>('in_shift');
  const [approvedMinutes, setApprovedMinutes] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [key] = useState<string>(newAdjustKey);

  const rProblem = reasonProblem(reason);
  const deltaSeconds = Math.round((deltaMinutes ?? 0) * 60);
  const deltaProblem =
    kind !== 'adjust' ? null : deltaSeconds === 0 ? 'Enter the minutes to add (positive) or remove (negative)' : Math.abs(deltaSeconds) > EMPLOYMENT_MAX_ADJUST_SECONDS ? 'At most 24 hours per adjustment' : null;
  const valid = !rProblem && !deltaProblem;

  const run = useMutation({
    mutationFn: async () => {
      const base = `/admin/listeners/${listenerId}/employment/attendance/${day.workDate}`;
      const headers = { 'idempotency-key': key };
      const r = reason.trim();
      switch (kind) {
        case 'adjust':
          return (await api.post<DayView>(`${base}/adjust`, { deltaSeconds, placement, reason: r }, { headers })).data;
        case 'approve':
          return (await api.post<DayView>(`${base}/overtime/approve`, { approvedSeconds: approvedMinutes === null ? null : Math.round(approvedMinutes * 60), reason: r }, { headers })).data;
        case 'reject':
          return (await api.post<DayView>(`${base}/overtime/reject`, { reason: r }, { headers })).data;
        case 'recompute':
          return (await api.post<DayView>(`${base}/recompute`, { reason: r }, { headers })).data;
      }
    },
    onSuccess: () => {
      message.success(`${ACTION_TITLE[kind]} — done for ${day.workDate}`);
      void qc.invalidateQueries({ queryKey: employmentKey(listenerId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-overview', listenerId] });
      onClose();
    },
    onError: (e: unknown) => message.error(writeError(e, 'Could not apply the change. Nothing was changed — you can try again.')),
  });

  const summaryLine =
    kind === 'adjust'
      ? `${deltaSeconds > 0 ? 'Add' : 'Remove'} ${formatDuration(Math.abs(deltaSeconds))} ${placement === 'in_shift' ? 'inside the shift' : 'after the shift'} on ${day.workDate}`
      : kind === 'approve'
        ? `Approve ${approvedMinutes === null ? `all ${formatDuration(day.overtimeSeconds)}` : formatDuration(Math.round(approvedMinutes * 60))} of overtime on ${day.workDate}`
        : kind === 'reject'
          ? `Reject the ${formatDuration(day.overtimeSeconds)} of overtime on ${day.workDate}`
          : `Recompute ${day.workDate} from the stored sessions with the terms in force`;

  return (
    <Modal
      title={ACTION_TITLE[kind]}
      open
      onCancel={run.isPending ? undefined : onClose}
      maskClosable={false}
      destroyOnHidden
      footer={
        step === 'form' ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!valid} onClick={() => setStep('confirm')}>
              Continue
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => setStep('form')} disabled={run.isPending}>
              Back
            </Button>
            <Button type="primary" danger={kind === 'reject'} loading={run.isPending} onClick={() => run.mutate()}>
              Confirm
            </Button>
          </Space>
        )
      }
    >
      {step === 'form' ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Date">{day.workDate}</Descriptions.Item>
            <Descriptions.Item label="Status">{plain(day.status)}</Descriptions.Item>
            <Descriptions.Item label="Worked">{formatDuration(day.workedSeconds)}</Descriptions.Item>
            <Descriptions.Item label="Overtime">{formatDuration(day.overtimeSeconds)}</Descriptions.Item>
          </Descriptions>
          {kind === 'adjust' && (
            <>
              <Field label="Minutes to add (negative = remove)">
                <InputNumber aria-label="Delta minutes" value={deltaMinutes} min={-1440} max={1440} onChange={(v) => setDeltaMinutes(v === null || v === undefined ? null : Number(v))} style={{ width: 160 }} />
                {deltaMinutes !== null && deltaProblem && <Typography.Text type="danger"> {deltaProblem}</Typography.Text>}
              </Field>
              <Field label="Placement" hint="Inside the shift fills missing time; after the shift counts as extra / overtime.">
                <Radio.Group value={placement} onChange={(e) => setPlacement(e.target.value as AdjustmentPlacement)}>
                  <Radio value="in_shift">Inside the shift</Radio>
                  <Radio value="after_shift">After the shift</Radio>
                </Radio.Group>
              </Field>
            </>
          )}
          {kind === 'approve' && (
            <Field label="Approve (minutes) — blank = all computed overtime">
              <InputNumber aria-label="Approved minutes" value={approvedMinutes} min={0} max={1440} onChange={(v) => setApprovedMinutes(v === null || v === undefined ? null : Number(v))} style={{ width: 160 }} />
            </Field>
          )}
          <Field label="Reason">
            <Input.TextArea aria-label="Action reason" rows={2} maxLength={EMPLOYMENT_REASON_MAX} showCount value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Kept in the audit log." />
            {reason && rProblem && <Typography.Text type="danger">{rProblem}</Typography.Text>}
          </Field>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {summaryLine}?
          </Typography.Title>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Reason">
              <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>“{reason.trim()}”</Typography.Text>
            </Descriptions.Item>
          </Descriptions>
          <Alert type="info" showIcon message="Recorded in the audit log with your name. The day's pay is re-settled by the server." />
        </Space>
      )}
    </Modal>
  );
}
